// src/components/billing/UsageDashboard.tsx
/**
 * Main usage dashboard component showing current usage, quotas, and history.
 * Inspired by Cursor's usage tracking design.
 */

import React, { useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Tabs,
  Tab,
  Button,
  Chip,
  Alert,
  Skeleton,
  useTheme,
  alpha,
  Divider,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TokenIcon from "@mui/icons-material/Token";
import DescriptionIcon from "@mui/icons-material/Description";
import MicIcon from "@mui/icons-material/Mic";
import EuroIcon from "@mui/icons-material/Euro";
import UpgradeIcon from "@mui/icons-material/Upgrade";
import { useUsage, useOrganization } from "../../hooks/useBilling";
import QuotaProgressBar from "./QuotaProgressBar";
import UsageChart from "./UsageChart";
import { useTranslation } from "../../i18n";
import { glass } from "../../theme/glassTokens";

interface UsageDashboardProps {
  onUpgradeClick?: () => void;
}

const UsageDashboard: React.FC<UsageDashboardProps> = ({ onUpgradeClick }) => {
  const theme = useTheme();
  const textPrimary = glass.textPrimary;
  const textSecondary = glass.textSecondary;
  const { t } = useTranslation();
  const { usage, history, breakdown, loading, error, refetch } = useUsage();
  const { organization } = useOrganization();
  const [chartMetric, setChartMetric] = useState<"cost" | "tokens" | "documents" | "voice_minutes">("cost");

  // Loading state
  if (loading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2, mb: 2 }} />
        <Grid container spacing={2}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} md={4} key={i}>
              <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2, "& .MuiAlert-message": { color: "error.dark" } }}>
        Failed to load usage data: {error}
        <Button onClick={() => refetch()} size="small" sx={{ ml: 2 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  if (!usage) return null;

  const tokens = usage.tokens || { is_unlimited: true, percentage_used: 0, used: 0, limit: 0 };
  const documents = usage.documents || { is_unlimited: true, percentage_used: 0, used: 0, limit: 0 };
  const voice_minutes = usage.voice_minutes || { is_unlimited: true, percentage_used: 0, used: 0, limit: 0 };

  // Calculate days remaining in period
  const daysRemaining = usage.period?.days_remaining ?? 0;
  
  // Determine if any quota is low
  const hasLowQuota = 
    (!tokens.is_unlimited && tokens.percentage_used >= 80) ||
    (!documents.is_unlimited && documents.percentage_used >= 80) ||
    (!voice_minutes.is_unlimited && voice_minutes.percentage_used >= 80);

  return (
    <Box>
      {/* Trial/Warning Banner */}
      {usage.is_trialing && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          action={
            onUpgradeClick && (
              <Button color="inherit" size="small" onClick={onUpgradeClick}>
                {t("billing.upgrade_now", "Upgrade Now")}
              </Button>
            )
          }
        >
          {t("billing.free_trial_remaining", "Free Trial: {{days}} days remaining. Upgrade to continue using all features.").replace("{{days}}", String(usage.trial_days_remaining))}
        </Alert>
      )}
      
      {hasLowQuota && !usage.is_trialing && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            onUpgradeClick && (
              <Button color="inherit" size="small" onClick={onUpgradeClick}>
                Upgrade
              </Button>
            )
          }
        >
          {t("billing.approaching_limits", "You're approaching your usage limits. Consider upgrading your plan for more capacity.")}
        </Alert>
      )}
      
      {/* Summary Card */}
      <Box
        sx={{
          mb: 3,
          p: { xs: 1.5, sm: 2.5 },
          borderRadius: 2,
          background: glass.panel,
          border: `1px solid ${alpha(glass.accent, 0.25)}`,
          backdropFilter: "blur(8px)",
          transition: "border-color 0.2s ease",
          "&:hover": { borderColor: alpha(glass.accent, 0.4) },
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={5}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: textPrimary }}>
                {usage.tier?.display_name || "Free"}
              </Typography>
              <Chip
                label={organization?.subscription_status || "active"}
                color={organization?.subscription_status === "active" ? "success" : "warning"}
                size="small"
                sx={{ ml: 2 }}
              />
            </Box>
            <Typography variant="body2" sx={{ color: textSecondary }}>
              {t("billing.billing_period", "Billing period:")} {usage.period?.start ? new Date(usage.period.start).toLocaleDateString() : "—"} - {usage.period?.end ? new Date(usage.period.end).toLocaleDateString() : "—"}
            </Typography>
            <Typography variant="body2" sx={{ color: textSecondary }}>
              {daysRemaining} {t("billing.days_remaining", "days remaining in this period")}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} sx={{ textAlign: { md: "center" } }}>
            <Typography variant="overline" sx={{ color: textSecondary, letterSpacing: 1.2 }}>
              {t("billing.current_period_cost", "Current Period Cost")}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: "success.main" }}>
              €{((usage.estimated_cost_cents || 0) / 100).toFixed(2)}
            </Typography>
          </Grid>
          <Grid item xs={12} md={3} sx={{ textAlign: { md: "right" } }}>
            {onUpgradeClick && (
              <Button
                variant="contained"
                startIcon={<UpgradeIcon />}
                onClick={onUpgradeClick}
              >
                {t("billing.upgrade_plan", "Upgrade Plan")}
              </Button>
            )}
          </Grid>
        </Grid>
      </Box>

      {/* Quota Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Tokens */}
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              p: { xs: 1.5, sm: 2.5 },
              borderRadius: 2,
              background: glass.panel,
              border: `1px solid ${glass.panelBorder}`,
              backdropFilter: "blur(8px)",
              transition: "border-color 0.2s ease",
              "&:hover": { borderColor: glass.headerDivider },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: alpha(glass.accent, 0.15),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 2,
                }}
              >
                <TokenIcon color="primary" />
              </Box>
              <Typography variant="h6" sx={{ color: textPrimary }}>{t("billing.llm_tokens", "LLM Tokens")}</Typography>
            </Box>
            <QuotaProgressBar quota={tokens} height={12} />
          </Box>
        </Grid>

        {/* Documents */}
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              p: { xs: 1.5, sm: 2.5 },
              borderRadius: 2,
              background: glass.panel,
              border: `1px solid ${glass.panelBorder}`,
              backdropFilter: "blur(8px)",
              transition: "border-color 0.2s ease",
              "&:hover": { borderColor: glass.headerDivider },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: alpha(theme.palette.secondary.main, 0.15),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 2,
                }}
              >
                <DescriptionIcon color="secondary" />
              </Box>
              <Typography variant="h6" sx={{ color: textPrimary }}>{t("billing.documents", "Documents")}</Typography>
            </Box>
            <QuotaProgressBar quota={documents} height={12} />
          </Box>
        </Grid>

        {/* Voice */}
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              p: { xs: 1.5, sm: 2.5 },
              borderRadius: 2,
              background: glass.panel,
              border: `1px solid ${glass.panelBorder}`,
              backdropFilter: "blur(8px)",
              transition: "border-color 0.2s ease",
              "&:hover": { borderColor: glass.headerDivider },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: alpha(theme.palette.success.light, 0.15),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 2,
                }}
              >
                <MicIcon color="success" />
              </Box>
              <Typography variant="h6" sx={{ color: textPrimary }}>Voice Minutes</Typography>
            </Box>
            <QuotaProgressBar quota={voice_minutes} height={12} />
          </Box>
        </Grid>
      </Grid>

      {/* Usage History Chart */}
      <Box
        sx={{
          mb: 3,
          p: { xs: 1.5, sm: 2.5 },
          borderRadius: 2,
          background: glass.panel,
          border: `1px solid ${glass.panelBorder}`,
          backdropFilter: "blur(8px)",
          transition: "border-color 0.2s ease",
          "&:hover": { borderColor: glass.headerDivider },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <TrendingUpIcon sx={{ mr: 1, color: textSecondary }} />
            <Typography variant="h6" sx={{ color: textPrimary }}>{t("billing.usage_history", "Usage History")}</Typography>
          </Box>
          <Tabs
            value={chartMetric}
            onChange={(_, v) => setChartMetric(v)}
            sx={{
              minHeight: 36,
              "& .MuiTab-root": { minHeight: 36, py: 0.5 },
            }}
          >
            <Tab
              value="cost"
              label={t("billing.cost", "Cost")}
              icon={<EuroIcon sx={{ fontSize: 16 }} />}
              iconPosition="start"
            />
            <Tab value="tokens" label={t("billing.tokens", "Tokens")} />
            <Tab value="documents" label={t("billing.documents", "Documents")} />
            <Tab value="voice_minutes" label={t("billing.voice", "Voice")} />
          </Tabs>
        </Box>
        
        {history.length > 0 ? (
          <UsageChart data={history} metric={chartMetric} height={280} />
        ) : (
          <Box
            sx={{
              height: 280,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: textSecondary,
            }}
          >
            {t("billing.no_usage_data", "No usage data yet. Start using the platform to see your history.")}
          </Box>
        )}
      </Box>

      {/* Usage Breakdown */}
      {breakdown.length > 0 && (
        <Box
          sx={{
            p: { xs: 1.5, sm: 2.5 },
            borderRadius: 2,
            background: glass.panel,
            border: `1px solid ${glass.panelBorder}`,
            backdropFilter: "blur(8px)",
            transition: "border-color 0.2s ease",
            "&:hover": { borderColor: glass.headerDivider },
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, color: textPrimary }}>
            Usage by Operation
          </Typography>
          <Grid container spacing={2}>
            {breakdown.map((item, idx) => (
              <Grid item xs={12} sm={6} md={3} key={idx}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background: glass.panel,
                  }}
                >
                  <Typography variant="body2" sx={{ color: textSecondary }} gutterBottom>
                    {item.type.replace(/_/g, " ").replace(/\b\w/g, (l: string) => l.toUpperCase())}
                  </Typography>
                  <Typography variant="h6" sx={{ color: "success.dark" }}>
                    €{(item.cost_cents / 100).toFixed(2)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: textSecondary }}>
                    {item.requests.toLocaleString()} requests &middot; {item.tokens.toLocaleString()} tokens
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default UsageDashboard;
